import sessionHandling from "services/utils/notificationUtils";
import { defaultAllSelection } from "services/constants";

// ESI Configuration APIs (Payroll > Configuration > Statutory > ESI, screens
// C1–C5a; ESI Frontend API Guide, base /api/payroll/statutory/esi/). The
// screens keep the esiConfigData.js shape ("Active", "21000", "" for empty,
// one components list); the mappers here convert to and from the API shape
// ("ACTIVE", "21000.00", null, calculation_ / contribution_components).

const BASE = "payroll/statutory/esi/";

// 409s the ESI API sends on purpose; any other 409 is the session check.
const ESI_CONFLICT_CODES = ["VERSION_LOCKED", "STALE_VERSION", "RUN_FINALISED"];

export class EsiApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.name = "EsiApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const errorText = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(errorText).join(" ");
  if (value && typeof value === "object") return Object.values(value).map(errorText).join(" ");
  return "";
};

// { wage_ceiling: ["…"], "components.3.formula": ["…"] } -> { wage_ceiling: "…", … }
const firstMessages = (error) =>
  error && typeof error === "object" && !Array.isArray(error)
    ? Object.fromEntries(Object.entries(error).map(([key, messages]) => [key, errorText(Array.isArray(messages) ? messages[0] : messages)]))
    : undefined;

// Shared by every ESI call (Statutory > ESI and Reports use it too). The
// guide's optional Idempotency-Key header is not sent: the server's CORS
// settings don't allow it, so the browser blocks every POST / PUT / DELETE
// that carries it. Save buttons show a loading state instead, so a double
// click can't send twice.
export const esiRequest = async ({ domain, path, method = "GET", query, body, signal }) => {
  const search = query ? new URLSearchParams(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")) : null;
  const url = `${domain.replace(/\/$/, "")}/${BASE}${path}${search && String(search) ? `?${search}` : ""}`;
  const response = await fetch(url, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${localStorage.getItem("token")}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new EsiApiError("Your session has expired. Please sign in again.", { status: 401 });
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (response.status === 409 && !ESI_CONFLICT_CODES.includes(data?.code)) sessionHandling();
  if (!response.ok) {
    throw new EsiApiError(errorText(data?.error || data?.detail || data) || `Request failed (${response.status}). Please try again.`, {
      status: response.status,
      code: data?.code,
      fieldErrors: firstMessages(data?.error),
    });
  }
  return data;
};

// ---- Value helpers ----

const text = (value) => (value == null ? "" : String(value));
// "21000.00" -> "21000"; "0.750" -> "0.75"; "21000.50" stays.
export const trimNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const out = String(value);
  return out.includes(".") ? out.replace(/0+$/, "").replace(/\.$/, "") : out;
};
const apiValue = (value) => (value === "" || value == null ? null : value);
const upper = (value) => String(value || "").toUpperCase();
const STATUS_WORD = { ACTIVE: "Active", INACTIVE: "Inactive" };
const statusWord = (value) => STATUS_WORD[upper(value)] || text(value);
const VERSION_STATUS_WORD = { DRAFT: "Draft", SAVED: "Active" };

// A list item that may be a string or an object with a name / label.
export const nameOf = (item) =>
  item == null ? "" : typeof item === "object" ? text(item.name ?? item.label ?? item.code ?? item.key ?? item.value) : text(item);

// Contribution period end: six months from the start (4 -> 9, 10 -> 3).
const periodEnd = (start) => ((Number(start) + 4) % 12) + 1;

// ---- Versions ----

export const fromApiVersion = (api) => {
  const calculations = [...(api.calculation_components || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const contributions = [...(api.contribution_components || [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  return {
    id: api.id ?? null,
    name: text(api.name),
    description: text(api.description),
    effective_from: text(api.effective_from),
    effective_to: text(api.effective_to),
    status: api.display_status || VERSION_STATUS_WORD[upper(api.status)] || "Draft",
    wage_ceiling: trimNumber(api.wage_ceiling),
    disability_ceiling: trimNumber(api.disability_ceiling),
    period1_start_month: text(api.period1?.start),
    period2_start_month: text(api.period2?.start),
    continue_to_period_end: api.continue_to_period_end ?? true,
    joiner_cover_from: text(api.joiner_cover_from),
    within_ceiling_cover_from: text(api.within_ceiling_cover_from),
    components: [
      ...calculations.map((component) => ({
        code: text(component.code),
        name: text(component.name),
        kind: "CALCULATION",
        description: text(component.description),
        formula: text(component.formula),
        amount_basis: text(component.amount_basis) || "EARNED",
        rounding: text(component.rounding) || "NONE",
        used_for: text(component.used_for),
        status: statusWord(component.status) || "Active",
      })),
      ...contributions.map((component) => ({
        code: text(component.code),
        name: text(component.name),
        kind: "CONTRIBUTION",
        description: text(component.description),
        type: text(component.type) || "EMPLOYEE_DEDUCTION",
        payslip_label: text(component.payslip_label),
        based_on: text(component.based_on),
        rate_pct: trimNumber(component.rate_pct),
        rounding: text(component.rounding) || "NONE",
        covered_only: component.covered_only ?? true,
        low_wage_enabled: Boolean(component.low_wage_enabled),
        low_wage_limit: trimNumber(component.low_wage_limit),
        advanced: Boolean(component.advanced),
        formula: component.advanced ? text(component.formula) : "",
        used_for: upper(component.type) === "EMPLOYER_COST" ? "Employer cost" : "Deduction",
        status: statusWord(component.status) || "Active",
      })),
    ],
    is_locked: Boolean(api.is_locked),
    used_by: (api.used_by || []).map(nameOf).filter(Boolean),
    source_version_id: api.source_version_id ?? null,
    row_version: api.row_version ?? null,
    created_by: text(api.created_by),
    created_at: text(api.created_at),
    updated_by: text(api.updated_by),
    updated_at: text(api.updated_at),
  };
};

// The version body shared by POST versions/, PUT versions/<id>/, PUT
// versions/draft/ and components/test/ (the unsaved version). Leaves out
// what the server sets (id, status, effective_to, is_locked, used_by).
export const toApiVersion = (form, companyId) => {
  const period = (start) => (start ? { start: Number(start), end: periodEnd(start) } : { start: null, end: null });
  const calculations = form.components.filter((component) => component.kind === "CALCULATION");
  const contributions = form.components.filter((component) => component.kind === "CONTRIBUTION");
  return {
    company_id: Number(companyId),
    name: text(form.name).trim(),
    description: text(form.description),
    effective_from: apiValue(form.effective_from),
    wage_ceiling: apiValue(form.wage_ceiling),
    disability_ceiling: apiValue(form.disability_ceiling),
    period1: period(form.period1_start_month),
    period2: period(form.period2_start_month),
    continue_to_period_end: Boolean(form.continue_to_period_end),
    joiner_cover_from: apiValue(form.joiner_cover_from),
    within_ceiling_cover_from: apiValue(form.within_ceiling_cover_from),
    calculation_components: calculations.map((component, index) => ({
      code: component.code,
      name: component.name,
      description: component.description || "",
      formula: component.formula,
      amount_basis: component.amount_basis,
      rounding: component.rounding,
      used_for: component.used_for || "",
      status: upper(component.status),
      sequence: index + 1,
    })),
    contribution_components: contributions.map((component, index) => ({
      code: component.code,
      name: component.name,
      type: component.type,
      based_on: component.based_on,
      rate_pct: apiValue(component.rate_pct),
      rounding: component.rounding,
      covered_only: Boolean(component.covered_only),
      low_wage_enabled: component.type === "EMPLOYEE_DEDUCTION" && Boolean(component.low_wage_enabled),
      low_wage_limit: component.type === "EMPLOYEE_DEDUCTION" && component.low_wage_enabled ? apiValue(component.low_wage_limit) : null,
      advanced: Boolean(component.advanced),
      ...(component.advanced ? { formula: component.formula } : {}),
      payslip_label: component.payslip_label || "",
      status: upper(component.status),
      sequence: index + 1,
    })),
  };
};

// Server field keys -> the screen's field keys (period1 -> Period 1 select;
// any components.N.field -> the components banner).
const toFormErrors = (fieldErrors) => {
  if (!fieldErrors) return undefined;
  const out = {};
  Object.entries(fieldErrors).forEach(([key, message]) => {
    const field = key.startsWith("period1") ? "period1_start_month" : key.startsWith("period2") ? "period2_start_month" : key.includes("components") ? "components" : key;
    out[field] = out[field] ? `${out[field]} ${message}` : message;
  });
  return out;
};

const withFormErrors = async (promise) => {
  try {
    return await promise;
  } catch (err) {
    if (err instanceof EsiApiError) err.fieldErrors = toFormErrors(err.fieldErrors);
    throw err;
  }
};

// Saved versions (oldest first) plus the draft, if there is one.
export const fetchEsiVersions = async ({ domain, companyId, signal }) => {
  const query = { company_id: companyId };
  const [saved, draft] = await Promise.all([
    esiRequest({ domain, path: "config/versions/", query, signal }),
    esiRequest({ domain, path: "config/versions/draft/", query, signal }).catch((err) => {
      if (err.status === 404) return null;
      throw err;
    }),
  ]);
  if (!Array.isArray(saved)) throw new EsiApiError("The server returned an invalid ESI configuration.");
  return [...saved.map(fromApiVersion), ...(draft ? [{ ...fromApiVersion(draft), status: "Draft" }] : [])];
};

// No version yet: a blank Version 1 from the server template (not saved).
export const fetchEsiDefaults = async ({ domain, companyId, signal }) => ({
  ...fromApiVersion(await esiRequest({ domain, path: "config/versions/defaults/", query: { company_id: companyId }, signal })),
  id: "new",
  status: "Draft",
});

// Create New Version: the version copied into the company's draft.
export const createEsiDraftFrom = async ({ domain, companyId, versionId }) => {
  const data = await esiRequest({ domain, path: `config/versions/draft/from/${versionId}/`, method: "POST", body: { company_id: Number(companyId) } });
  return { version: { ...fromApiVersion(data.data), status: "Draft" }, message: data.message };
};

// Auto-save while a draft is edited: only formats are checked.
export const saveEsiDraft = ({ domain, companyId, form, signal }) =>
  esiRequest({ domain, path: "config/versions/draft/", method: "PUT", body: toApiVersion(form, companyId), signal });

export const discardEsiDraft = async ({ domain, companyId }) => {
  try {
    await esiRequest({ domain, path: "config/versions/draft/", method: "DELETE", query: { company_id: companyId } });
  } catch (err) {
    if (err.status !== 404) throw err;
  }
};

const saved = (data) => ({ version: fromApiVersion(data.data), message: data.message });

// Save & Activate: the draft's content becomes a new version.
export const createEsiVersion = ({ domain, companyId, form }) =>
  withFormErrors(esiRequest({ domain, path: "config/versions/", method: "POST", body: toApiVersion(form, companyId) }).then(saved));

// Save a saved, unlocked version.
export const updateEsiVersion = ({ domain, companyId, form }) =>
  withFormErrors(
    esiRequest({
      domain,
      path: `config/versions/${form.id}/`,
      method: "PUT",
      body: { ...toApiVersion(form, companyId), row_version: form.row_version },
    }).then(saved),
  );

// ---- Options, setup checklist, formula check, test ----

const optionList = (list) =>
  Array.isArray(list) ? list.map((item) => (typeof item === "object" ? { value: text(item.value ?? item.key ?? item.code), label: text(item.label ?? item.name ?? item.value) } : { value: text(item), label: text(item) })) : null;
const codeList = (list) => (Array.isArray(list) ? list.map(nameOf).filter(Boolean) : []);

export const fetchEsiOptions = async ({ domain, companyId, signal }) => {
  const data = (await esiRequest({ domain, path: "config/options/", query: { company_id: companyId }, signal })) || {};
  const chips = data.formula_chips || {};
  return {
    joiner_cover_from: optionList(data.joiner_cover_from),
    within_ceiling_cover_from: optionList(data.within_ceiling_cover_from),
    amount_basis: optionList(data.amount_basis),
    rounding: optionList(data.rounding),
    contribution_types: optionList(data.contribution_types),
    chips: {
      salary: codeList(chips.salary),
      pay_input: codeList(chips.pay_input),
      runtime: codeList(chips.runtime),
      functions: codeList(chips.functions),
    },
  };
};

export const fetchEsiStatus = ({ domain, companyId, signal }) => esiRequest({ domain, path: "config/status/", query: { company_id: companyId }, signal });

// Always 200: { valid, message, final_formula, codes }.
export const checkEsiFormulaOnServer = ({ domain, companyId, body, signal }) =>
  esiRequest({ domain, path: "config/formula/check/", method: "POST", body: { company_id: Number(companyId), ...body }, signal });

export const testEsiComponent = ({ domain, companyId, employeeId, month, form, componentCode, signal }) =>
  esiRequest({
    domain,
    path: "config/components/test/",
    method: "POST",
    body: {
      company_id: Number(companyId),
      employee_id: Number(employeeId),
      month,
      version: toApiVersion(form, companyId),
      component_code: componentCode,
    },
    signal,
  });

// ---- Employer codes ----

// last_esic_file / last_challan stay null until the Reports plan.
const lastText = (value) => (value == null ? "" : typeof value === "object" ? text(value.label ?? value.name ?? "") : text(value));

// "51001234560000001" -> "51-00-123456-000-0001" (2-2-6-3-4), for when
// the server sent no code_display.
export const dashedCode = (code) => {
  const digits = String(code || "").replace(/D/g, "");
  return digits.length === 17 ? `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 10)}-${digits.slice(10, 13)}-${digits.slice(13)}` : text(code);
};
const displayOf = (code) => (code ? text(code.code_display) || dashedCode(code.code) : "");

// A site on a code: {id, name, code (SITE-01), is_active, employees, from}.
const fromApiSite = (site) => ({
  id: String(site.id),
  name: text(site.name),
  code: text(site.code),
  is_active: site.is_active !== false,
  employees: Number(site.employees) || 0,
  from: text(site.from),
});
// "Erode Unit 1 (SITE-01)"
export const siteLabel = (site) => (site ? (site.code ? `${site.name} (${site.code})` : site.name) : "");

export const fromApiCode = (api) => {
  const sites = (api.sites || []).map(fromApiSite);
  return {
    id: api.id,
    code: text(api.code),
    code_display: displayOf(api),
    type: upper(api.type) || "SUB",
    parent_id: api.parent_id ?? null,
    branch_name: text(api.branch_name),
    coverage_from: text(api.coverage_from),
    branch_office: text(api.branch_office),
    address: text(api.address),
    status: statusWord(api.status) || "Active",
    inactive_from: text(api.inactive_from),
    row_version: api.row_version ?? null,
    main_code: api.main_code ? { ...api.main_code, code_display: displayOf(api.main_code) } : null,
    employees: Number(api.employees) || 0,
    covered_employees: api.covered_employees ?? null,
    sites,
    site_ids: sites.map((site) => site.id),
    last_file: lastText(api.last_esic_file),
    last_challan: lastText(api.last_challan),
  };
};

export const fetchEmployerCodes = async ({ domain, companyId, signal }) => {
  const data = (await esiRequest({ domain, path: "config/employer-codes/", query: { company_id: companyId }, signal })) || {};
  return {
    cards: data.cards || {},
    codes: (data.codes || []).map(fromApiCode),
    // Sites with employees of this company and no ESI code.
    unlinked: (data.sites_not_linked || []).map((site) => ({ ...fromApiSite(site), label: siteLabel(fromApiSite(site)) })),
  };
};

// The Sites covered table (every site). With codeId, the code's own sites
// come back ticked.
export const fetchCodeSites = async ({ domain, companyId, codeId, signal }) => {
  const data = await esiRequest({ domain, path: "config/employer-codes/sites/", query: { company_id: companyId, code_id: codeId }, signal });
  return (Array.isArray(data) ? data : []).map((site) => ({
    ...fromApiSite(site),
    state: text(site.state),
    checked: Boolean(site.checked),
    disabled: Boolean(site.disabled),
    disabled_reason: site.disabled_reason || null,
    in_code: site.in_code ? { ...site.in_code, code_display: displayOf(site.in_code) } : null,
  }));
};

export const previewCodeLinks = ({ domain, companyId, codeId, siteIds, signal }) =>
  esiRequest({
    domain,
    path: "config/employer-codes/link-preview/",
    method: "POST",
    body: { company_id: Number(companyId), ...(codeId ? { code_id: codeId } : {}), site_ids: siteIds.map(Number) },
    signal,
  });

const codeErrors = (fieldErrors) =>
  fieldErrors && Object.fromEntries(Object.entries(fieldErrors).map(([key, message]) => [key === "site_ids" ? "sites" : key, message]));

export const saveEmployerCode = async ({ domain, companyId, form }) => {
  const editing = form.id != null;
  try {
    const data = await esiRequest({
      domain,
      path: editing ? `config/employer-codes/${form.id}/` : "config/employer-codes/",
      method: editing ? "PUT" : "POST",
      body: {
        company_id: Number(companyId),
        code: form.code.trim(),
        type: form.type,
        branch_name: form.branch_name.trim(),
        coverage_from: form.coverage_from,
        branch_office: form.branch_office.trim(),
        address: form.address.trim(),
        site_ids: form.site_ids.map(Number),
        status: upper(form.status),
        ...(editing ? { row_version: form.row_version } : {}),
      },
    });
    return { code: fromApiCode(data.data), message: data.message };
  } catch (err) {
    if (err instanceof EsiApiError) err.fieldErrors = codeErrors(err.fieldErrors);
    throw err;
  }
};

export const deactivateEmployerCode = async ({ domain, companyId, code }) => {
  const data = await esiRequest({
    domain,
    path: `config/employer-codes/${code.id}/deactivate/`,
    method: "POST",
    body: { company_id: Number(companyId), row_version: code.row_version },
  });
  return { code: fromApiCode(data.data), message: data.message };
};

// ---- Reason codes ----

// A mapped chip: exit reason / leave type (id) or situation (key).
const chip = (group, item) => {
  const id = group === "situation" ? text(item?.key ?? item) : text(item?.id ?? item);
  return { value: `${group}:${id}`, label: nameOf(item) || id, group, id };
};

export const fromApiReasonCode = (api) => {
  const chips = [
    ...(api.exit_reasons || []).map((item) => chip("exit", item)),
    ...(api.leave_types || []).map((item) => chip("leave", item)),
    ...(api.situations || []).map((item) => chip("situation", item)),
  ];
  return {
    id: api.code,
    code: api.code,
    label: text(api.label),
    lwd: Boolean(api.lwd_required),
    removes: Boolean(api.removes_from_portal),
    is_out_of_coverage: Boolean(api.is_out_of_coverage),
    chips,
    reasons: chips.map((item) => item.label),
    reasons_label: chips.map((item) => item.label).join(", "),
    lwd_label: api.lwd_required ? "Required" : "No",
    removes_label: api.removes_from_portal ? "Yes" : "No",
    status: text(api.status) || (chips.length ? "Mapped" : "Not used"),
  };
};

export const fromApiReasonCodes = (data) => {
  const unmapped = (data?.unmapped_exit_reasons || []).map(nameOf).filter(Boolean);
  const warnings = (data?.warnings || []).map((item) => (typeof item === "string" ? item : nameOf(item) || text(item?.message))).filter(Boolean);
  return {
    verifiedNote: text(data?.verified_note),
    cards: data?.cards || {},
    codes: (data?.codes || []).map(fromApiReasonCode),
    warnings: warnings.length
      ? warnings
      : unmapped.map((name) => `Exit reason “${name}” (People › Exit reasons) is not mapped. Employees leaving with this reason will be flagged in Validation until it is mapped.`),
  };
};

export const fetchReasonCodes = async ({ domain, companyId, signal }) =>
  fromApiReasonCodes(await esiRequest({ domain, path: "config/reason-codes/", query: { company_id: companyId }, signal }));

const GROUP_HINT = { exit: "Exit reason", leave: "Leave type", situation: "Situation" };

// One code with every chip that can be picked and the code it is on now.
export const fetchReasonCode = async ({ domain, companyId, code, signal }) => {
  const data = await esiRequest({ domain, path: `config/reason-codes/${code}/`, query: { company_id: companyId }, signal });
  const available = data.available || {};
  const option = (group, item) => {
    const base = chip(group, item);
    const owner = item?.mapped_to;
    const elsewhere = owner != null && Number(owner) !== Number(code);
    return {
      ...base,
      disabled: elsewhere || Boolean(item?.system),
      hint: item?.system ? "Set by the system" : elsewhere ? `Mapped to code ${owner}` : GROUP_HINT[group],
    };
  };
  const options = [
    ...(available.exit_reasons || []).map((item) => option("exit", item)),
    ...(available.leave_types || []).map((item) => option("leave", item)),
    ...(available.situations || []).map((item) => option("situation", item)),
  ];
  const current = fromApiReasonCode(data);
  // Mapped chips stay pickable even if the server leaves them out of
  // `available`.
  current.chips.forEach((item) => {
    if (!options.some((entry) => entry.value === item.value)) options.push({ ...item, hint: GROUP_HINT[item.group] });
  });
  return {
    ...current,
    canMap: data.can_map !== false,
    options,
    usedRecently: data.used_recently ?? null,
  };
};

export const saveReasonMapping = async ({ domain, companyId, code, values }) => {
  const ids = (group) => values.filter((value) => value.startsWith(`${group}:`)).map((value) => value.slice(group.length + 1));
  const data = await esiRequest({
    domain,
    path: `config/reason-codes/${code}/mapping/`,
    method: "PUT",
    body: {
      company_id: Number(companyId),
      exit_reason_ids: ids("exit").map(Number),
      leave_type_ids: ids("leave").map(Number),
      situations: ids("situation"),
    },
  });
  return { data: fromApiReasonCodes(data.data), message: data.message };
};

// ---- Employees for "Test on employee" (shared employee list API) ----

export const fetchTestEmployees = async ({ domain, companyId, signal }) => {
  const response = await fetch(`${domain}list/employee?company_id=${companyId}&department_id=${defaultAllSelection}&status=Active`, {
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
  });
  if (!response.ok) throw new EsiApiError("Couldn't load the employees.", { status: response.status });
  const data = await response.json().catch(() => []);
  return (Array.isArray(data) ? data : [])
    .map((item) => ({
      id: String(item.user_id ?? item.id ?? ""),
      name: text(item.user_name || item.username || item.name),
      code: text(item.employee_id ?? item.emp_id ?? item.employee_code ?? ""),
    }))
    .filter((item) => item.id && item.name);
};
